import React, { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";

type AuthModalType = "signIn" | "signUp";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: AuthModalType;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, type }) => {
  // Close modal when user successfully signs in or signs up
  const { isSignedIn } = useAuth();

  useEffect(() => {
    // If the user is authenticated and the modal is open, close it
    if (isSignedIn && isOpen) {
      onClose();
    }
  }, [isSignedIn, isOpen, onClose]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-middle bg-white rounded-lg overflow-hidden shadow-xl transform transition-all my-8 max-w-md w-full p-0">
              <div className="bg-white px-6 pt-6 pb-6">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {type === "signIn" ? "Sign In" : "Sign Up"}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="mt-2 flex justify-center">
                  {type === "signIn" ? (
                    <SignIn
                      routing="virtual"
                      signUpUrl="#sign-up"
                      afterSignInUrl="/dashboard"
                      redirectUrl="/dashboard"
                      appearance={{
                        elements: {
                          rootBox: "w-full flex justify-center items-center",
                          card: "w-full max-w-md p-6 bg-white rounded-lg shadow-none border border-gray-200",
                          formButtonPrimary: "w-full mt-4",
                          headerTitle: "hidden",
                          headerSubtitle: "hidden",
                          dividerLine: "hidden",
                          dividerText: "hidden",
                          footer: "hidden",
                        },
                      }}
                    />
                  ) : (
                    <SignUp
                      routing="virtual"
                      signInUrl="#sign-in"
                      afterSignUpUrl="/dashboard"
                      redirectUrl="/dashboard"
                      appearance={{
                        elements: {
                          rootBox: "w-full flex justify-center items-center",
                          card: "w-full max-w-md p-6 bg-white rounded-lg shadow-none border border-gray-200",
                          formButtonPrimary: "w-full mt-4",
                          headerTitle: "hidden",
                          headerSubtitle: "hidden",
                          dividerLine: "hidden",
                          dividerText: "hidden",
                          footer: "hidden",
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AuthModal;
